import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_CAT = { name: "", description: "", sort_order: 0, active: true };
const EMPTY_GRP = { name: "", description: "", sort_order: 0, active: true, min_age: "", deposit_amount: 0 };
const EMPTY_EXTRA = { name: "", description: "", price: 0, sort_order: 0, active: true };

const NOUN_BASE = {
  Kategorie: "/admin/vehicle-categories",
  Gruppe: "/admin/vehicle-groups",
  Zusatzleistung: "/admin/extras",
};
const NOUN_EMPTY = { Kategorie: EMPTY_CAT, Gruppe: EMPTY_GRP, Zusatzleistung: EMPTY_EXTRA };

function ItemTable({ items, onEdit, onDelete, noun, showPrice, showConditions }) {
  if (!items.length)
    return <p className="text-[#525252] text-sm py-6 text-center">Noch keine {noun} angelegt.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F4F4] text-xs uppercase tracking-wider text-[#525252]">
          <tr>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Beschreibung</th>
            {showPrice && <th className="text-left p-3">Preis/Tag</th>}
            {showConditions && <th className="text-left p-3">Mindestalter</th>}
            {showConditions && <th className="text-left p-3">Kaution</th>}
            <th className="text-left p-3">Reihenfolge</th>
            <th className="text-left p-3">Status</th>
            <th className="text-right p-3">Aktionen</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E5E5E5]">
          {items.map((item) => (
            <tr key={item.id} className="hover:bg-[#FAFAFA]">
              <td className="p-3 font-semibold text-[#0A0A0A]">{item.name}</td>
              <td className="p-3 text-[#525252]">{item.description || "—"}</td>
              {showPrice && <td className="p-3 text-[#525252]">{Number(item.price || 0).toFixed(2)}€</td>}
              {showConditions && <td className="p-3 text-[#525252]">{item.min_age ? `${item.min_age} Jahre` : "—"}</td>}
              {showConditions && <td className="p-3 text-[#525252]">{Number(item.deposit_amount || 0).toFixed(2)}€</td>}
              <td className="p-3 text-[#525252]">{item.sort_order}</td>
              <td className="p-3">
                <Badge className={item.active ? "bg-emerald-100 text-emerald-800 border-0" : "bg-[#F4F4F4] text-[#525252] border-0"}>
                  {item.active ? "Aktiv" : "Inaktiv"}
                </Badge>
              </td>
              <td className="p-3">
                <div className="flex gap-2 justify-end">
                  <Button size="icon" variant="ghost" onClick={() => onEdit(item)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => onDelete(item)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ItemDialog({ open, onClose, editing, onSave, noun }) {
  const [form, setForm] = useState(editing || { ...NOUN_EMPTY[noun] });
  const [saving, setSaving] = useState(false);
  const isExtra = noun === "Zusatzleistung";
  const isGroup = noun === "Gruppe";

  useEffect(() => {
    setForm(editing || { ...NOUN_EMPTY[noun] });
  }, [editing, noun]);

  const f = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name ist Pflichtfeld"); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? `${noun} bearbeiten` : `${noun} anlegen`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => f("name")(e.target.value)}
              className="mt-1" placeholder={noun === "Kategorie" ? "z.B. PKW" : noun === "Gruppe" ? "z.B. X4" : "z.B. Navigation"} />
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Textarea value={form.description || ""} onChange={(e) => f("description")(e.target.value)}
              className="mt-1" rows={2} />
          </div>
          {isExtra && (
            <div>
              <Label>Preis pro Tag (€)</Label>
              <Input type="number" step="0.5" value={form.price} onChange={(e) => f("price")(parseFloat(e.target.value) || 0)}
                className="mt-1" />
            </div>
          )}
          {isGroup && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mindestalter (Jahre)</Label>
                <Input type="number" min="0" value={form.min_age ?? ""} placeholder="Keine Vorgabe"
                  onChange={(e) => f("min_age")(e.target.value === "" ? null : parseInt(e.target.value) || 0)}
                  className="mt-1" />
              </div>
              <div>
                <Label>Kaution (€)</Label>
                <Input type="number" step="10" value={form.deposit_amount} onChange={(e) => f("deposit_amount")(parseFloat(e.target.value) || 0)}
                  className="mt-1" />
              </div>
            </div>
          )}
          <div>
            <Label>Reihenfolge</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => f("sort_order")(parseInt(e.target.value) || 0)}
              className="mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="active-chk" checked={form.active}
              onChange={(e) => f("active")(e.target.checked)} className="accent-[#E11226]" />
            <Label htmlFor="active-chk">Aktiv</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E11226] hover:bg-[#C20E1F]">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminGroups() {
  const [tab, setTab] = useState("kategorien");
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [extras, setExtras] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    const [cats, grps, exts] = await Promise.all([
      api.get("/admin/vehicle-categories"),
      api.get("/admin/vehicle-groups"),
      api.get("/admin/extras"),
    ]);
    setCategories(cats.data);
    setGroups(grps.data);
    setExtras(exts.data);
  };

  useEffect(() => { load(); }, []);

  const nounForTab = { kategorien: "Kategorie", gruppen: "Gruppe", extras: "Zusatzleistung" }[tab];

  const openNew = (noun) => setDialog({ noun, editing: null });
  const openEdit = (noun, item) => setDialog({ noun, editing: item });
  const closeDialog = () => setDialog(null);

  const handleSave = async (form) => {
    const base = NOUN_BASE[dialog.noun];
    if (dialog.editing) {
      await api.put(`${base}/${dialog.editing.id}`, form);
      toast.success(`${dialog.noun} aktualisiert`);
    } else {
      await api.post(base, form);
      toast.success(`${dialog.noun} angelegt`);
    }
    await load();
  };

  const handleDelete = async (noun, item) => {
    if (!window.confirm(`"${item.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`${NOUN_BASE[noun]}/${item.id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="rf-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#E11226] font-semibold">Flotte</div>
          <h1 className="font-display text-3xl font-bold text-[#0A0A0A] mt-1">Gruppen, Kategorien & Extras</h1>
        </div>
        <Button
          onClick={() => openNew(nounForTab)}
          className="bg-[#E11226] hover:bg-[#C20E1F]"
        >
          <Plus className="w-4 h-4 mr-2" />
          {nounForTab} anlegen
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E5E5] mb-6">
        {[
          { key: "kategorien", label: "Kategorien", count: categories.length },
          { key: "gruppen", label: "Gruppen", count: groups.length },
          { key: "extras", label: "Zusatzleistungen", count: extras.length },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-[#E11226] text-[#0A0A0A]"
                : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs text-[#525252]">({t.count})</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        {tab === "kategorien" && (
          <ItemTable
            items={categories}
            noun="Kategorien"
            onEdit={(item) => openEdit("Kategorie", item)}
            onDelete={(item) => handleDelete("Kategorie", item)}
          />
        )}
        {tab === "gruppen" && (
          <ItemTable
            items={groups}
            noun="Gruppen"
            showConditions
            onEdit={(item) => openEdit("Gruppe", item)}
            onDelete={(item) => handleDelete("Gruppe", item)}
          />
        )}
        {tab === "extras" && (
          <ItemTable
            items={extras}
            noun="Zusatzleistungen"
            showPrice
            onEdit={(item) => openEdit("Zusatzleistung", item)}
            onDelete={(item) => handleDelete("Zusatzleistung", item)}
          />
        )}
      </div>

      {dialog && (
        <ItemDialog
          open
          onClose={closeDialog}
          editing={dialog.editing}
          noun={dialog.noun}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
